import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import { FormLayout, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import { AddButtonHolder } from '@/components/Testing';
import useAlerts from '@/hooks/useAlerts';
import { RemoveButton } from '@/pages/InvoiceList/components/InvoicesMemberModal';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

type FormDataT = {
  files: { file?: File }[];
};

const StyledAddButtonHolder = styled(AddButtonHolder)`
  margin: 0;
`;

const FormFieldHolder = styled.div`
  grid-column: span 2;
`;

const StyledRemoveButton = styled(RemoveButton)`
  width: 100%;
  margin-top: 24px;
`;

interface Props {
  onClose: () => void;
}

const FreelancerFilesModal: FC<Props> = ({ onClose }) => {
  const queryClient = useQueryClient();

  const alerts = useAlerts();

  const { id } = useParams();

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      files: [{}],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'files',
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async ({ files }) => {
      try {
        const formData = new FormData();

        if (files) {
          files.forEach((file, index) => {
            if (file.file) {
              formData.append(`files[${index}][file]`, file.file);
            }
          });
        }

        const response = await api.post<FormData>(
          `/v1/payments/freelancers/files/${id}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        );

        queryClient.invalidateQueries(['freelancers_details'], {
          exact: false,
        });

        if (response) {
          reset();
          alerts.success('Files were successfully added');

          return onClose();
        }
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  return (
    <Modal stopPropagation={false}>
      <SidePanel heading="Upload freelancer files" onClose={onClose}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              {fields.map((_, index) => (
                <FormFieldHolder key={index}>
                  <FormField<FormDataT>
                    label={`File nr.${index + 1}`}
                    name={`files.${index}.file`}
                    type="file"
                    control={control}
                    id={`file-${index}`}
                    notes="PDF, doc, docx, xls, xlsx, png, jpg, jpeg / Size: Up to 20MB"
                    accept=".doc, .docx,application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/pdf, image/png, image/jpeg, image/jpg"
                  />
                  {index > 0 && (
                    <StyledRemoveButton
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(index);
                      }}
                    >
                      Remove section
                    </StyledRemoveButton>
                  )}
                </FormFieldHolder>
              ))}
              {!!errors.files && (
                <Larger>
                  <ValidationMessage>
                    {errors.files.message as string}
                  </ValidationMessage>
                </Larger>
              )}
              <Larger>
                <StyledAddButtonHolder>
                  <Button
                    type="button"
                    $type="colorized"
                    onClick={() => append({})}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8 2V14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2 8H14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Add other file
                  </Button>
                </StyledAddButtonHolder>
              </Larger>
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              Save
            </Button>
            <Button
              color="secondary"
              onClick={(e: MouseEvent) => {
                e.preventDefault();
                onClose();
              }}
            >
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default FreelancerFilesModal;
